import React, { useState, useEffect } from "react";
import { useT } from "../i18n";
import { trackEvent } from "../analytics";

const API = process.env.REACT_APP_BACKEND_URL;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const RECAPTCHA_ACTION = "lead_note_submit";

const INPUT_CLS =
  "w-full px-3.5 py-2.5 rounded-lg border border-[#D2D2D7] bg-white text-[14px] text-[#1D1D1F] placeholder:text-[#86868B] focus:outline-none focus:border-[#023CFA] focus:ring-2 focus:ring-[#023CFA]/15 transition-colors";
const LABEL_CLS = "block text-[12px] font-semibold text-[#1D1D1F] mb-1.5";

// Load Google reCAPTCHA v3 script on demand (dedupes if already loaded).
function loadRecaptcha() {
  return new Promise((resolve, reject) => {
    if (!RECAPTCHA_SITE_KEY) return reject(new Error("Missing site key"));
    if (window.grecaptcha && window.grecaptcha.execute) return resolve(window.grecaptcha);
    const existing = document.querySelector("script[data-recaptcha]");
    if (existing) {
      existing.addEventListener("load", () => resolve(window.grecaptcha));
      existing.addEventListener("error", reject);
      return;
    }
    const s = document.createElement("script");
    s.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(RECAPTCHA_SITE_KEY)}`;
    s.async = true;
    s.defer = true;
    s.dataset.recaptcha = "true";
    s.onload = () => resolve(window.grecaptcha);
    s.onerror = () => reject(new Error("reCAPTCHA failed to load"));
    document.head.appendChild(s);
  });
}

/**
 * Lead-note form — shown as an alternative to the Calendly widget for people
 * who aren't ready to book a call. Submits to POST /api/lead-note.
 */
export default function LeadNoteForm({ onSuccess }) {
  const { t } = useT();
  const [values, setValues] = useState({
    name: "",
    email: "",
    phone: "",
    community: "",
    message: "",
    website: "", // honeypot
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [serverMsg, setServerMsg] = useState("");

  // Preload reCAPTCHA so submit is instant.
  useEffect(() => {
    if (RECAPTCHA_SITE_KEY) loadRecaptcha().catch(() => {});
  }, []);

  const set = (k) => (e) => setValues((v) => ({ ...v, [k]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (!values.name.trim()) errs.name = t("leadNote.err.nameRequired");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email))
      errs.email = t("leadNote.err.emailInvalid");
    if (values.message.trim().length < 5)
      errs.message = t("leadNote.err.messageShort");
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status === "submitting") return;
    if (!validate()) return;
    setStatus("submitting");
    setServerMsg("");
    try {
      // Fetch fresh reCAPTCHA v3 token (single-use, ~2 min TTL).
      let recaptchaToken = null;
      if (RECAPTCHA_SITE_KEY) {
        try {
          const g = await loadRecaptcha();
          recaptchaToken = await new Promise((resolve, reject) => {
            g.ready(() =>
              g.execute(RECAPTCHA_SITE_KEY, { action: RECAPTCHA_ACTION }).then(resolve).catch(reject)
            );
          });
        } catch (e) {
          console.warn("[lead-note] recaptcha token error:", e);
        }
      }
      const res = await fetch(`${API}/api/lead-note`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: values.name.trim(),
          email: values.email.trim(),
          phone: values.phone.trim() || null,
          community: values.community.trim() || null,
          message: values.message.trim(),
          website: values.website, // honeypot
          recaptcha_token: recaptchaToken,
        }),
      });
      if (res.status === 429) {
        setStatus("error");
        setServerMsg(t("leadNote.err.rateLimit"));
        return;
      }
      if (res.status === 403) {
        setStatus("error");
        setServerMsg(t("leadNote.err.generic"));
        return;
      }
      if (!res.ok) {
        setStatus("error");
        setServerMsg(t("leadNote.err.generic"));
        return;
      }
      trackEvent("lead_note_sent", {});
      setStatus("success");
      if (onSuccess) onSuccess();
    } catch (err) {
      console.warn("[lead-note] submit failed:", err);
      setStatus("error");
      setServerMsg(t("leadNote.err.network"));
    }
  };

  if (status === "success") {
    return (
      <div
        data-testid="lead-note-success"
        className="rounded-2xl border border-[#E5E7EB] bg-[#F5F7FA] p-6 text-center"
      >
        <div className="text-[15px] font-semibold text-[#0A1428] mb-1">
          {t("leadNote.success.title")}
        </div>
        <p className="text-[13px] text-[#515154] leading-relaxed max-w-md mx-auto">
          {t("leadNote.success.body")}
        </p>
      </div>
    );
  }

  return (
    <form
      data-testid="lead-note-form"
      onSubmit={onSubmit}
      className="grid grid-cols-1 md:grid-cols-2 gap-3.5"
      noValidate
    >
      {/* Honeypot — hidden from real users */}
      <label
        aria-hidden="true"
        className="absolute -left-[9999px] w-px h-px overflow-hidden"
      >
        Website
        <input
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={values.website}
          onChange={set("website")}
        />
      </label>

      <div>
        <label className={LABEL_CLS} htmlFor="ln-name">
          {t("leadNote.field.name")} *
        </label>
        <input
          id="ln-name"
          data-testid="lead-note-name"
          type="text"
          className={INPUT_CLS}
          value={values.name}
          onChange={set("name")}
          required
          maxLength={120}
        />
        {errors.name && (
          <p className="mt-1 text-[11px] text-[#B42318]">{errors.name}</p>
        )}
      </div>

      <div>
        <label className={LABEL_CLS} htmlFor="ln-email">
          {t("leadNote.field.email")} *
        </label>
        <input
          id="ln-email"
          data-testid="lead-note-email"
          type="email"
          className={INPUT_CLS}
          value={values.email}
          onChange={set("email")}
          required
          maxLength={200}
        />
        {errors.email && (
          <p className="mt-1 text-[11px] text-[#B42318]">{errors.email}</p>
        )}
      </div>

      <div>
        <label className={LABEL_CLS} htmlFor="ln-phone">
          {t("leadNote.field.phone")}{" "}
          <span className="font-normal text-[#86868B]">
            ({t("leadNote.field.optional")})
          </span>
        </label>
        <input
          id="ln-phone"
          data-testid="lead-note-phone"
          type="tel"
          className={INPUT_CLS}
          value={values.phone}
          onChange={set("phone")}
          maxLength={40}
        />
      </div>

      <div>
        <label className={LABEL_CLS} htmlFor="ln-community">
          {t("leadNote.field.community")}{" "}
          <span className="font-normal text-[#86868B]">
            ({t("leadNote.field.optional")})
          </span>
        </label>
        <input
          id="ln-community"
          data-testid="lead-note-community"
          type="text"
          className={INPUT_CLS}
          value={values.community}
          onChange={set("community")}
          maxLength={160}
        />
      </div>

      <div className="md:col-span-2">
        <label className={LABEL_CLS} htmlFor="ln-message">
          {t("leadNote.field.message")} *
        </label>
        <textarea
          id="ln-message"
          data-testid="lead-note-message"
          rows={4}
          className={`${INPUT_CLS} resize-y min-h-[110px]`}
          value={values.message}
          onChange={set("message")}
          required
          maxLength={2000}
        />
        {errors.message && (
          <p className="mt-1 text-[11px] text-[#B42318]">{errors.message}</p>
        )}
      </div>

      {status === "error" && serverMsg && (
        <div
          data-testid="lead-note-error"
          className="md:col-span-2 rounded-lg border border-[#F2C4C1] bg-[#FFF4F3] px-3 py-2 text-[12px] text-[#B42318]"
          role="alert"
        >
          {serverMsg}
        </div>
      )}

      <div className="md:col-span-2 flex items-center justify-end gap-3 pt-1">
        <button
          type="submit"
          data-testid="lead-note-submit"
          disabled={status === "submitting"}
          className="inline-flex items-center bg-[#023CFA] hover:bg-[#1E52FF] text-white px-3.5 py-1.5 rounded-full text-[12px] font-normal disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
          style={{
            letterSpacing: "-0.01em",
            transition: "background-color 300ms cubic-bezier(0.16, 1, 0.3, 1)",
            transform: "translateZ(0)",
            backfaceVisibility: "hidden",
          }}
        >
          {status === "submitting"
            ? t("leadNote.submit.sending")
            : t("leadNote.submit.send")}
        </button>
      </div>
    </form>
  );
}
